import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import api from "@/api/client";
import { Mail, Lock, Phone, Sprout, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import SeoHead from "@/components/seo/SeoHead";

const Login = () => {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { otpLogin } = useAuth();

  // Default landing post-login; ?next= overrides for "Buy Now"-style flows.
  const postLogin = (u) => {
    if (next && next.startsWith("/")) return navigate(next);
    navigate(u.role === "seller" ? "/seller/dashboard" : "/dashboard");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { user: u, autoRegistered } = await login(email, password);
      if (autoRegistered) {
        toast.success(`Welcome ${u.name}! Your account was created automatically.`);
      } else {
        toast.success(`Welcome back, ${u.name}`);
      }
      postLogin(u);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const sendOtp = async () => {
    if (!/^\+?\d{10,15}$/.test(phone.replace(/\s/g, ""))) { setErr("Enter a valid phone number"); return; }
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      toast.success(data.message);
      setOtpSent(true);
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await otpLogin(phone, otp, "Farmer");
      toast.success(`Welcome ${u.name}`);
      postLogin(u);
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] container-fah py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center" data-testid="page-login">
      <SeoHead title="Login" description="Sign in to your FarmersAgroHub account to track orders, manage your wishlist and shop from GST-verified agriculture sellers." path="/login" noindex />
      <div className="hidden lg:block">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center"><Sprout className="w-7 h-7 text-white" /></div>
          <div>
            <div className="font-display font-extrabold text-xl">FarmersAgroHub</div>
            <div className="text-xs text-muted-foreground">{t("Connecting Farmers, Empowering Agriculture")}</div>
          </div>
        </div>
        <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight text-balance">{t("Login to access India's #1 agriculture marketplace")}</h1>
        <p className="mt-4 text-muted-foreground max-w-lg">{t("Manage your orders, track deliveries, save favorite products, and shop from thousands of GST-verified agri-input sellers.")}</p>
        <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
          <div className="card-fah p-4"><div className="text-2xl font-bold text-primary">18,000+</div><div className="text-xs text-muted-foreground">{t("Pincodes served")}</div></div>
          <div className="card-fah p-4"><div className="text-2xl font-bold text-primary">5,000+</div><div className="text-xs text-muted-foreground">{t("Verified sellers")}</div></div>
        </div>
      </div>

      <div className="card-fah p-6 sm:p-8 max-w-md w-full mx-auto" data-testid="login-card">
        <h2 className="font-display text-2xl font-bold">{t("Welcome back")}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t("Choose how you'd like to sign in")}</p>
        <div className="mt-3 text-xs bg-accent/40 text-primary rounded-lg p-2.5 border border-primary/20 flex items-start gap-2">
          <Sprout className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{t("New here? Just enter your email & password — your account will be created automatically.")}</span>
        </div>

        <div className="mt-5 flex bg-muted/60 rounded-lg p-1">
          <button onClick={() => setTab("email")} className={`flex-1 py-2 rounded-md text-sm font-semibold ${tab === "email" ? "bg-white shadow text-primary" : "text-muted-foreground"}`} data-testid="tab-email">{t("Email")}</button>
          <button onClick={() => setTab("otp")} className={`flex-1 py-2 rounded-md text-sm font-semibold ${tab === "otp" ? "bg-white shadow text-primary" : "text-muted-foreground"}`} data-testid="tab-otp">{t("Mobile OTP")}</button>
        </div>

        {err && <div className="mt-4 text-sm bg-destructive/10 text-destructive p-3 rounded-lg" data-testid="login-error">{err}</div>}

        {tab === "email" ? (
          <form onSubmit={doLogin} className="mt-5 space-y-4" data-testid="form-email-login">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("Email")}</label>
              <div className="mt-1 flex items-center border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <Mail className="w-4 h-4 text-muted-foreground ml-3" />
                <input data-testid="input-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("Password")}</label>
              <div className="mt-1 flex items-center border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <Lock className="w-4 h-4 text-muted-foreground ml-3" />
                <input data-testid="input-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm" />
              </div>
            </div>
            <button disabled={loading} type="submit" className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="btn-login-submit">
              {loading ? t("Signing in…") : <>{t("Sign In")} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={otpSent ? verifyOtp : (e) => { e.preventDefault(); sendOtp(); }} className="mt-5 space-y-4" data-testid="form-otp-login">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("Mobile Number")}</label>
              <div className="mt-1 flex items-center border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <Phone className="w-4 h-4 text-muted-foreground ml-3" />
                <input data-testid="input-phone" required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" disabled={otpSent} className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm disabled:opacity-60" />
              </div>
            </div>
            {otpSent && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("Enter OTP (try 123456)")}</label>
                <input data-testid="input-otp" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit OTP" className="mt-1 w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-center tracking-[0.5em] font-bold text-lg" />
              </div>
            )}
            <button disabled={loading} type="submit" className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 rounded-lg disabled:opacity-50" data-testid="btn-otp-submit">
              {loading ? t("Please wait…") : (otpSent ? t("Verify OTP") : t("Send OTP"))}
            </button>
            {otpSent && <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }} className="w-full text-xs text-muted-foreground hover:text-primary">{t("Change number")}</button>}
          </form>
        )}

        <div className="mt-5 text-center text-sm">
          {t("New to FarmersAgroHub?")} <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="link-register">{t("Create an account")}</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
